import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, Users } from "lucide-react";
import investorPortalApi from "@/lib/investorPortalApi";

const formatDate = (iso, lang = "de") => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "en" ? "en-US" : "de-DE", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
};

const STATUS_BADGE = {
  new: "bg-skyMist text-cocoa",
  duplicate: "bg-butterCream text-cocoa",
  contacted: "bg-sageMint text-cocoa",
  joined: "bg-lavenderFog text-cocoa",
};

const EMPTY_FORM = {
  referred_name: "", referred_email: "", referred_linkedin: "",
  referred_organization: "", investor_type_suggestion: "", reason: "",
  attribution: "named", consent: false,
};

export default function InvestorReferral() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "de";
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    investorPortalApi.get("/refer/history")
      .then(({ data }) => setHistory(data.history || []))
      .finally(() => setHistoryLoading(false));
  }, []);

  const upd = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent || submitting) return;
    setSubmitting(true);
    try {
      await investorPortalApi.post("/refer", form);
      toast.success(t("invPortal.referral.success"));
      setSubmitted(true);
      // Refresh history
      const { data } = await investorPortalApi.get("/refer/history");
      setHistory(data.history || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error(t("invPortal.referral.rateLimited"));
      else if (status === 400) toast.error(t("invPortal.referral.consentRequired"));
      else toast.error(t("invPortal.referral.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => { setForm({ ...EMPTY_FORM }); setSubmitted(false); };

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-6" data-testid="investor-portal-referral">
      <motion.header initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: "easeOut" }}>
        <p className="wit-eyebrow text-raspberry mb-2">{t("invPortal.referral.eyebrow")}</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-cocoa leading-[1.05]">{t("invPortal.referral.title")}</h1>
        <p className="mt-3 text-warmGrey">{t("invPortal.referral.lead")}</p>
      </motion.header>

      {/* Referral Form */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.05 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="rounded-3xl border border-sandBeige bg-white p-6 sm:p-7 shadow-soft relative overflow-hidden"
        data-testid="inv-referral-form-section"
      >
        <div className="absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl bg-butterCream" aria-hidden />
        <p className="wit-eyebrow text-raspberry mb-2">{t("invPortal.referral.formEyebrow")}</p>
        <h2 className="font-serif text-xl text-cocoa mb-5">{t("invPortal.referral.formTitle")}</h2>

        {submitted ? (
          <div className="text-center py-6" data-testid="inv-referral-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-sageMint flex items-center justify-center text-cocoa mb-4">
              <Check size={24} />
            </div>
            <p className="font-serif text-2xl text-cocoa">{t("invPortal.referral.successTitle")}</p>
            <p className="text-warmGrey text-sm mt-2">{t("invPortal.referral.successBody")}</p>
            <button type="button" onClick={resetForm}
              className="mt-5 bg-raspberry hover:bg-coral text-white text-sm rounded-full px-5 py-2.5 transition-colors"
              data-testid="inv-referral-another"
            >
              {t("invPortal.referral.referAnother")}
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4" data-testid="inv-referral-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-warmGrey uppercase tracking-wide">{t("invPortal.referral.name")} *</label>
                <input required type="text" value={form.referred_name} onChange={(e) => upd("referred_name", e.target.value)}
                  className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm text-cocoa"
                  data-testid="inv-referral-name" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-warmGrey uppercase tracking-wide">{t("invPortal.referral.email")} *</label>
                <input required type="email" value={form.referred_email} onChange={(e) => upd("referred_email", e.target.value)}
                  className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm text-cocoa"
                  data-testid="inv-referral-email" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-warmGrey uppercase tracking-wide">{t("invPortal.referral.linkedin")}</label>
                <input type="url" value={form.referred_linkedin} onChange={(e) => upd("referred_linkedin", e.target.value)}
                  placeholder="https://linkedin.com/in/…"
                  className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm text-cocoa"
                  data-testid="inv-referral-linkedin" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-warmGrey uppercase tracking-wide">{t("invPortal.referral.org")}</label>
                <input type="text" value={form.referred_organization} onChange={(e) => upd("referred_organization", e.target.value)}
                  className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm text-cocoa"
                  data-testid="inv-referral-org" />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-warmGrey uppercase tracking-wide">{t("invPortal.referral.reason")}</label>
              <textarea value={form.reason} onChange={(e) => upd("reason", e.target.value)} rows={3}
                placeholder={t("invPortal.referral.reasonPlaceholder")}
                className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm text-cocoa resize-none"
                data-testid="inv-referral-reason" />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-medium text-warmGrey uppercase tracking-wide">{t("invPortal.referral.attribution")}</label>
              <div className="flex gap-3">
                {["named", "anonymous"].map((opt) => (
                  <button key={opt} type="button" onClick={() => upd("attribution", opt)}
                    className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                      form.attribution === opt ? "bg-raspberry text-white border-raspberry" : "bg-porcelain text-cocoa border-sandBeige hover:border-raspberry"
                    }`}
                    data-testid={`inv-referral-attr-${opt}`}
                  >
                    {t(`invPortal.referral.attr_${opt}`)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-warmGrey">{t("invPortal.referral.attributionHint")}</p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer mt-2" data-testid="inv-referral-consent">
              <input type="checkbox" checked={form.consent} onChange={(e) => upd("consent", e.target.checked)}
                className="mt-0.5 w-5 h-5 rounded border-sandBeige text-raspberry focus:ring-raspberry" />
              <span className="text-sm text-cocoa">{t("invPortal.referral.consentText")}</span>
            </label>

            <button type="submit" disabled={submitting || !form.consent}
              className="self-start bg-raspberry hover:bg-coral disabled:opacity-60 text-white text-sm rounded-full px-6 py-3 inline-flex items-center gap-2 transition-colors"
              data-testid="inv-referral-submit"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />}
              {submitting ? t("invPortal.referral.submitting") : t("invPortal.referral.submit")}
            </button>
          </form>
        )}
      </motion.section>

      {/* Referral History */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.05 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="rounded-3xl border border-sandBeige bg-white p-6 sm:p-7 shadow-soft relative overflow-hidden"
        data-testid="inv-referral-history-section"
      >
        <div className="absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl bg-skyMist" aria-hidden />
        <p className="wit-eyebrow text-raspberry mb-2">{t("invPortal.referral.historyEyebrow")}</p>
        <h2 className="font-serif text-xl text-cocoa mb-4">{t("invPortal.referral.historyTitle")}</h2>

        {historyLoading ? (
          <div className="flex items-center justify-center text-warmGrey gap-2 py-4">
            <Loader2 className="animate-spin" size={18} />
          </div>
        ) : history.length === 0 ? (
          <p className="text-warmGrey text-sm">{t("invPortal.referral.historyEmpty")}</p>
        ) : (
          <ul className="flex flex-col gap-3" data-testid="inv-referral-history-list">
            {history.map((r) => (
              <li key={r.id} className="flex items-center gap-3 p-3 rounded-2xl bg-porcelain border border-sandBeige" data-testid={`inv-referral-${r.id}`}>
                <div className="w-10 h-10 rounded-full bg-butterCream flex items-center justify-center text-cocoa text-sm font-medium">
                  {r.referred_initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-cocoa font-medium">{r.referred_initials}</p>
                    {r.referred_organization && <p className="text-xs text-warmGrey">@ {r.referred_organization}</p>}
                  </div>
                  <p className="text-xs text-warmGrey">{formatDate(r.created_at, lang)}</p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wide font-medium ${STATUS_BADGE[r.status] || STATUS_BADGE.new}`}>
                  {t(`invPortal.referral.status_${r.status}`, { defaultValue: r.status })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </motion.section>
    </div>
  );
}
